import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    Users,
    Mail,
    Send,
    Check,
    AlertCircle,
    Loader2,
    Search,
    Ticket
} from 'lucide-react';
import { useSelector } from 'react-redux';

const GuestList = ({ eventId, eventTitle, totalTickets, soldTickets, freeTicketsPercentage, unlimitedInvitations = false }) => {
    const { slug } = useParams();
    const [email, setEmail] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }
    const [invitations, setInvitations] = useState([]);
    const [fetchingList, setFetchingList] = useState(false);

    // Calcular límite de free tickets
    const maxFree = unlimitedInvitations ? null : Math.floor(totalTickets * ((freeTicketsPercentage || 10) / 100));
    const usedFree = invitations.length; // Aproximación (hacer fetch real si es paginado)
    const availableFree = unlimitedInvitations ? null : Math.max(0, maxFree - usedFree);
    const hasAvailableInvitations = unlimitedInvitations || availableFree > 0;

    useEffect(() => {
        if (eventId) {
            fetchInvitations();
        }
    }, [eventId]);

    const fetchInvitations = async () => {
        setFetchingList(true);
        try {
            // TODO: Crear endpoint GET /invitations o filtrar en tickets
            // Por ahora simulamos o asumimos que hay un endpoint
            // const res = await axios.get(`/api/events/${eventId}/invitations`);
            // setInvitations(res.data);
            // MOCK DATA TEMPORALMENTE HASTA TENER EL ENDPOINT GET
            setInvitations([]);
        } catch (err) {
            console.error(err);
        } finally {
            setFetchingList(false);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            const res = await axios.post(`/api/invitations/assign`, {
                eventId,
                userEmail: email,
                quantity: Number(quantity),
                notes
            });

            setMessage({ type: 'success', text: `¡Éxito! ${res.data.message}` });
            setEmail('');
            setQuantity(1);
            setNotes('');
            fetchInvitations(); // Recargar lista
        } catch (err) {
            setMessage({
                type: 'error',
                text: err.response?.data?.error || 'Error al enviar invitación.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Resumen de Cupo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400">
                        <Users size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-bold text-white/40">Cupo Invitaciones</p>
                        <p className="text-xl font-black text-white">{unlimitedInvitations ? 'Sin limite' : maxFree}</p>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center text-green-400">
                        <Check size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-bold text-white/40">Emitidas</p>
                        <p className="text-xl font-black text-white">{usedFree}</p>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-orange/20 rounded-xl flex items-center justify-center text-primary-orange">
                        <Ticket size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-bold text-white/40">Disponibles</p>
                        <p className="text-xl font-black text-white">{unlimitedInvitations ? 'Sin limite' : availableFree}</p>
                    </div>
                </div>
            </div>

            {/* Formulario */}
            <div className="bg-glass border border-white/5 rounded-3xl p-6 md:p-8">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <Mail size={20} className="text-primary-orange" />
                    Enviar Invitación
                </h3>

                <form onSubmit={handleSend} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-white/40 uppercase mb-1 block">Email del Usuario</label>
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="usuario@email.com"
                                className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-primary-orange/50 transition-all placeholder:text-white/10"
                                required
                            />
                            <p className="text-[10px] text-white/20 mt-1">El usuario debe estar registrado en Passar.</p>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-white/40 uppercase mb-1 block">Cantidad</label>
                            <input
                                type="number"
                                value={quantity}
                                onChange={e => setQuantity(Number(e.target.value))}
                                min="1"
                                {...(!unlimitedInvitations ? { max: availableFree > 0 ? availableFree : 1 } : {})}
                                className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-primary-orange/50 transition-all font-mono"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-white/40 uppercase mb-1 block">Notas (Opcional)</label>
                        <input
                            type="text"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Ej: Amigo del dueño, Sorteo Instagram..."
                            className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-primary-orange/50 transition-all placeholder:text-white/10"
                        />
                    </div>

                    {message && (
                        <div className={`p-3 rounded-xl flex items-center gap-2 text-sm font-bold ${message.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                            {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                            {message.text}
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={loading || !hasAvailableInvitations}
                            className="bg-white text-black px-6 py-3 rounded-xl font-black text-sm uppercase tracking-wider hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                            Enviar Invitación
                        </button>
                    </div>
                </form>
            </div>

            {/* Lista (Placeholder hasta tener backend GET) */}
            <div className="bg-glass border border-white/5 rounded-3xl p-6 opacity-50">
                <h3 className="text-lg font-bold text-white mb-2">Historial de Invitaciones</h3>
                <p className="text-white/40 text-sm italic">La lista de invitados aparecerá aquí...</p>
            </div>

        </div>
    );
};

export default GuestList;
